import React, { useEffect, useState, useRef } from "react";
import styles from "./Notifications.module.css";
import { Button } from "react-bootstrap";
import { DEFAULT_PAYLOAD, PageNav, Pagination } from "../../common/Table";
import { getUserDetails } from "../../../backend/auth";
import dayjs from "dayjs";
import NotificationCard from "./NotificationCard";
import { useGetAllNotifications, useGetNotificationCountsByCategory } from "../../../backend/notifications";
import DatePicker from "react-multi-date-picker";
import { humanReadableNumber, toBackendDateFormat } from "../../../utils/common";
import MastersLayout from "../Masters/MastersLayout";

const Range: any = {
    LAST_10D: 'Last 10 Days',
    LAST_1W: 'Last 1 Week',
    LAST_1M: 'Last 1 Month'
}

const Category: any = {
    PUSH: { value: 'Push', label: 'Push Notifications', key: 'pushCounts' },
    WORK: { value: 'Work', label: 'Work Notifications', key: 'workCounts' },
    BACKEND: { value: 'Backend', label: 'Backend Notifications', key: 'backEndCounts' }
}

export default function NotificationsCenter() {
    const [breadcrumb] = useState([
        { id: 'home', label: 'Home', path: '/' },
        { id: 'notifications', label: 'Notification Center', path: '/notifications' }
    ]);
    const datePickerRef = useRef<any>();
    const [range, setRange] = useState(Range.LAST_10D);
    const [category, setCategory] = useState<any>();
    const [payload, setPayload] = useState<any>();
    const [payloadForCounts, setPayloadForCounts] = useState<any>();
    const [customDate, setCustomDate] = useState<any>();
    const customDateRef = useRef();
    customDateRef.current = customDate;
    const [dateRange, setDateRange] = useState<any>();
    const { notifications, total, isFetching, refetch  }: any = useGetAllNotifications(payload, Boolean(payload));
    const { counts }: any = useGetNotificationCountsByCategory(payloadForCounts, Boolean(payloadForCounts));

    // Pagination
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [per, setPageSize] = useState({ value: 25, label: 25 });
    const [pageCounter, setPageCounter] = useState('');

    function handlePageSizeChange(e: any) {
        setPageSize(e);
        setPage(1);
        setPayload({
            ...payload,
            page: 1, 
            per: e.value
        });
    }

    function handlePageNav(pageNav: string) {
        let _page = 1;
        switch (pageNav) {
            case PageNav.FIRST:
                _page = 1;
                break;
            case PageNav.PREVIOUS:
                _page = page - 1;
                break;
            case PageNav.NEXT:
                _page = page + 1;
                break;
            case PageNav.LAST:
                _page = lastPage * 1;
                break;
            default:
                break;
        }
        setPage(_page);
        setPayload({
            ...payload,
            page: _page, 
            per: per.value
        });
    }

    function handleRangeChange(_range: string) {
        if (range !== _range) {
            setRange(_range);
            setCustomDate(null);
        }
    }

    function getDateRange(_range: string) {
        if (!_range) {
            return {};
        }
        let from: any = new Date();
        let to: any = new Date();
        if (range === Range.LAST_10D) {
            from.setDate(from.getDate() - 10);
        } else if (range === Range.LAST_1W) {
            from.setDate(from.getDate() - 7);
        } else if (range === Range.LAST_1M) {
            from.setDate(from.getMonth() - 1);
        }
        return {
            fromDate: toBackendDateFormat(dayjs(from).startOf('D').toDate()),
            toDate: toBackendDateFormat(dayjs(to).endOf('D').toDate())
        }
    }

    function handleCategoryChange(_category: string) {
        setCategory(category !== _category ? _category : null);
    }

    function handleClearSelection(event: any) {
        setCustomDate(undefined);
        setDateRange(undefined);
        setRange(Range.LAST_10D);
        datePickerRef.current.closeCalendar();
    }

    function handleDateRangeSelection() {
        setRange(undefined);
        datePickerRef.current.closeCalendar();
        if (customDateRef.current) {
            const _filters: any = {};
            let _fromDate, _toDate;
            if (Array.isArray(customDateRef.current)) {
                _fromDate = new Date(customDateRef.current[0]);
                _toDate = new Date(customDateRef.current[1] || customDateRef.current[0]);
            } else {
                _fromDate = new Date(customDateRef.current);
                _toDate = new Date(customDateRef.current);
            }
            setDateRange({ fromDate: _fromDate, toDate: _toDate });
            if (_fromDate) {
                _filters['created_at_gteq'] = toBackendDateFormat(dayjs(_fromDate).startOf('D').toDate());
            }
            if (_toDate) {
                _filters['created_at_lteq'] = toBackendDateFormat(dayjs(_toDate).endOf('D').toDate());
            }
            setPage(1);
            setPayload({
                ...payload, 
                filters: {
                  ...payload.filters,
                  ..._filters
                },
                per: per.value,
                page: 1
            });
            setPayloadForCounts({
                ...payloadForCounts, 
                filters: {
                  ...payloadForCounts.filters,
                  ..._filters
                }
            });
        }
    }

    useEffect(() => {
        if (range || category) {
            const user = getUserDetails();
            const filters: any = {};
            filters['user_id_eq'] = user.user_id;
            const { fromDate, toDate } = range ? getDateRange(range) : dateRange;
            if (fromDate) {
              
                filters['created_at_gteq'] = fromDate;
            }
            if (toDate) {
                filters['created_at_lteq'] = toDate;
            }
            const filtersForCounts = { ...filters };
            if (category) {
                filters['category_eq'] = category;
            }
            setPage(1);
            setPayload({
                ...DEFAULT_PAYLOAD, 
                filters,
                per: per.value,
                page: 1
            });
            setPayloadForCounts({
                ...DEFAULT_PAYLOAD, 
                ...{ filters: filtersForCounts },
                per: per.value,
                page: 1
            });
        }
    }, [range, category]);

    useEffect(() => {
        if (!isFetching && notifications) {
            const _lastPage = Math.ceil(total / (per.value || 1)) || 1;
            setLastPage(_lastPage);
            const startIndex = (page - 1) * per.value + 1;
            const lastIndex = page * per.value;
            setPageCounter(total ? `Showing ${startIndex} - ${lastIndex > total ? total : lastIndex} of ${humanReadableNumber(total || 0)} records` : '');
        }
    }, [isFetching])

    return (
        <>
            <MastersLayout title="Notification Center" breadcrumbs={breadcrumb}>
                <div className="d-flex flex-column mx-0" >
                    <div className="d-flex flex-column gap-3 m-3">
                        <div className="d-flex flex-row gap-3">
                            {
                                Object.keys(Range).map((key: string, index: number) => {
                                    return (
                                        <Button variant={range === Range[key] ? "primary" : "default"}
                                            className={range === Range[key] ? "no-shadow" : "bg-white"}
                                            onClick={() => handleRangeChange(Range[key])} key={index}>{Range[key]}</Button>
                                    )
                                })
                            }
                            <div className="date-picker col-3">
                                <DatePicker ref={datePickerRef} range={true}
                                    format={'DD/MM/YYYY'} value={customDate}
                                    className="date-field" placeholder="Custom Date Range"
                                    onChange={setCustomDate}>
                                    <div className="d-flex flex-row justify-content-center gap-2 mb-2">
                                        <Button variant="link" onClick={handleClearSelection}>Clear</Button>
                                        <Button variant="primary" onClick={handleDateRangeSelection} disabled={!customDate}>Select</Button>
                                    </div>
                                </DatePicker>
                            </div>
                        </div>
                        <div className="d-flex flex-row gap-3">
                            {
                                Object.values(Category).map(({ value, label, key }: any) => {
                                    return (
                                        <Button variant={category === value ? "primary" : "outline-primary"}
                                            onClick={() => handleCategoryChange(value)} key={key}>{label} ({counts && counts[value] || 0})</Button>
                                    )
                                })
                            }
                        </div>
                    </div>
                    <div className={`d-flex flex-column m-0 py-2 bg-white ${styles.notificationsContainer}`}>
                        {
                            (notifications || []).map((notification: any) => {
                                return (
                                    <NotificationCard key={notification.id} notification={notification} onSubmit={refetch} />
                                )
                            })
                        }
                    </div>
                    {
                        Boolean(pageCounter) &&
                        <div className="d-flex flex-row w-100">
                            <Pagination pageCounter={pageCounter} page={page} lastPage={lastPage} per={per}
                                handlePageSizeChange={handlePageSizeChange} handlePageNav={handlePageNav} className="position-relative" />
                        </div>
                    }
                </div>
            </MastersLayout>
        </>
    )
}
